import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, ShieldCheck } from "lucide-react";

import axios from "axios";
import { API_BASE, formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_LABELS } from "./erLabels";

// Anonymous flow uses raw axios (no auth token) — this route is publicly reachable.
const publicApi = axios.create({ baseURL: API_BASE, timeout: 20000 });

export default function ErAnonymousIntake() {
  const navigate = useNavigate();
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [parties, setParties] = useState("");
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    publicApi.get("/campuses")
      .then((r) => setCampuses(r.data))
      .catch(() => setCampuses([]));
  }, []);

  const canSubmit = useMemo(
    () => category && description.trim().length > 10 && !saving,
    [category, description, saving],
  );

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const { data } = await publicApi.post("/er/intake", {
        reporter_type: "anonymous",
        category,
        description: description.trim(),
        parties_involved: parties.trim() || null,
        campus_id: campusId || null,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const copy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(`${result.case_number} — ${result.token}`);
      setCopied(true);
      toast.success("Copied to clipboard.");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Please select and copy manually.");
    }
  };

  return (
    <div className="min-h-screen bg-sage-200 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="er-anon-intake-page">
        <div>
          <button
            onClick={() => navigate("/login")}
            className="inline-flex items-center text-forest-700 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            Back to sign-in
          </button>
        </div>

        <div className="patria-card p-8">
          <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
            Patria Church — People Operations
          </p>
          <h1
            className="font-serif text-forest-900 text-4xl mt-2"
            data-testid="er-anon-intake-headline"
          >
            Anonymous Report
          </h1>
          <p className="mt-3 text-charcoal-soft max-w-2xl leading-relaxed">
            You don&apos;t need an account to submit this. We will not collect your email or
            connect this report to any user record. When you submit, you&apos;ll receive a case
            number and a token — save both so you can check on your report later.
          </p>

          {!result && (
            <section className="mt-8 space-y-5" data-testid="er-anon-intake-form">
              <div>
                <label className="patria-label" htmlFor="ac">Category *</label>
                <select
                  id="ac"
                  data-testid="er-anon-category"
                  className="patria-input mt-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">Choose a category…</option>
                  {Object.entries(CATEGORY_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="patria-label" htmlFor="ad">What happened? *</label>
                <textarea
                  id="ad"
                  data-testid="er-anon-description"
                  className="patria-input mt-2"
                  rows={7}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Share what happened, when, and how it affected you or others. The more detail, the better we can help."
                />
                <p className="mt-1 text-xs text-charcoal-muted">Minimum 10 characters.</p>
              </div>
              <div>
                <label className="patria-label" htmlFor="ap">Parties involved (optional)</label>
                <textarea
                  id="ap"
                  data-testid="er-anon-parties"
                  className="patria-input mt-2"
                  rows={2}
                  value={parties}
                  onChange={(e) => setParties(e.target.value)}
                />
              </div>
              <div>
                <label className="patria-label" htmlFor="acamp">Campus (optional)</label>
                <select
                  id="acamp"
                  data-testid="er-anon-campus"
                  className="patria-input mt-2"
                  value={campusId}
                  onChange={(e) => setCampusId(e.target.value)}
                >
                  <option value="">— unspecified —</option>
                  {campuses.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="pt-4 flex justify-end border-t border-sage-300/60">
                <button
                  className="patria-btn-primary"
                  disabled={!canSubmit}
                  onClick={submit}
                  data-testid="er-anon-submit"
                >
                  {saving ? "Submitting…" : "Submit anonymously"}
                </button>
              </div>
            </section>
          )}

          {result && (
            <section
              className="mt-8 space-y-6"
              data-testid="er-anon-confirmation"
            >
              <div className="p-5 rounded-2xl bg-forest-600 text-cream-100">
                <div className="flex items-start gap-3">
                  <ShieldCheck className="h-5 w-5 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <p className="font-serif text-2xl">Your Report Is In.</p>
                    <p className="text-sm mt-1 text-cream-100/85">
                      Save this case number and token in a safe place. You&apos;ll need
                      <em> both </em> to check on your report or send messages.
                    </p>
                  </div>
                </div>

                <div className="mt-5 rounded-xl bg-cream-100 text-charcoal p-5 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">Case Number</p>
                    <p
                      className="font-mono font-medium text-forest-900 text-lg"
                      data-testid="er-anon-result-case"
                    >
                      {result.case_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-charcoal-muted">Token (Shown Once)</p>
                    <p
                      className="font-mono font-medium text-forest-900 text-lg break-all"
                      data-testid="er-anon-result-token"
                    >
                      {result.token}
                    </p>
                  </div>
                  <button
                    onClick={copy}
                    className="patria-btn-secondary text-sm py-2 px-3"
                    data-testid="er-anon-copy"
                  >
                    <Copy className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
                    {copied ? "Copied" : "Copy case + token"}
                  </button>
                </div>
              </div>

              <p className="text-sm text-charcoal-soft">
                {result.token_note}
              </p>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/er/anonymous/portal"
                  className="patria-btn-primary"
                  data-testid="er-anon-go-portal"
                >
                  Go to the anonymous check-in portal
                </Link>
                <button
                  onClick={() => navigate("/login")}
                  className="patria-btn-secondary"
                >
                  Return to sign-in
                </button>
              </div>
            </section>
          )}
        </div>

        <p className="text-xs text-charcoal-muted text-center italic">
          Patria Church strictly prohibits retaliation for good-faith reporting.
        </p>
      </div>
    </div>
  );
}
